import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
} from '@angular/core/rxjs-interop';
import { SimplifiedUser } from '@galaxy/crm/static';
import { FieldValueInterface } from '@vendasta/crm';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import {
  BlankValueComponent,
  CustomCellComponent,
} from '@vendasta/galaxy/table';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CRMUserService } from '../../user.service';
import { StandardIds } from '@galaxy/crm/services/field-service';

const NO_VALUE_FOR_TEAM_MEMBER = '<NO_VALUE_FOR_TEAM_MEMBER>';

@Component({
  template: `
    <ng-container *ngIf="teamMember() as teamMember; else loading">
      <div class="team-member" *ngIf="teamMember !== NO_VALUE_FOR_TEAM_MEMBER; else blank">
        <div class="tags">
          <glxy-badge [glxyTooltip]="teamMemberNames().join(', ')">{{ teamMemberNames()[0] }}</glxy-badge>
          <glxy-badge *ngIf="teamMemberNames().length === 2" [glxyTooltip]="teamMemberNames().join(', ')">
            {{ teamMemberNames()[1] }}
          </glxy-badge>
          <glxy-badge *ngIf="teamMemberNames().length > 2" [glxyTooltip]="teamMemberNames().join(', ')">
            +{{ teamMemberNames().length - 1 }}
          </glxy-badge>
        </div>
      </div>
    </ng-container>

    <ng-template #loading>
      <div class="stencil-shimmer"></div>
    </ng-template>
    <ng-template #blank>
      <glxy-blank-value></glxy-blank-value>
    </ng-template>
  `,
  styleUrls: ['./team-member.component.scss'],
  imports: [CommonModule, BlankValueComponent, GalaxyBadgeModule, GalaxyTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamMemberCellComponent extends CustomCellComponent {
  NO_VALUE_FOR_TEAM_MEMBER = NO_VALUE_FOR_TEAM_MEMBER;
  fieldValues = signal([]);
  set value(newValues: (FieldValueInterface | null)[]) {
    this.fieldValues.set(newValues);
  }
  teamMember: WritableSignal<SimplifiedUser[] | '<NO_VALUE_FOR_TEAM_MEMBER>' | null> = signal(null);
  teamMemberNames = computed(() => {
    const teamMember = this.teamMember();
    if (teamMember === NO_VALUE_FOR_TEAM_MEMBER) {
      return [];
    }
    return teamMember.map((tm) => tm.displayName);
  });

  constructor(private readonly userService: CRMUserService) {
    super();
    toObservable(this.fieldValues)
      .pipe(
        switchMap((fieldValues) => {
          const teamMemberFieldValue =
            fieldValues.find(
              (fieldValue) =>
                fieldValue?.fieldId === StandardIds.CompanyAdditionalTeamMemberIDs ||
                fieldValue?.fieldId === StandardIds.ContactAdditionalTeamMemberIDs,
            ) ?? null;
          if (!teamMemberFieldValue) {
            return of(NO_VALUE_FOR_TEAM_MEMBER);
          }
          const teamMemberIds = teamMemberFieldValue.stringValues?.values ?? null;
          if (!teamMemberIds.length) {
            return of(NO_VALUE_FOR_TEAM_MEMBER);
          }
          return this.userService.getMultiUsers(teamMemberIds);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((result) => {
        if (result === NO_VALUE_FOR_TEAM_MEMBER) {
          this.teamMember.set(NO_VALUE_FOR_TEAM_MEMBER);
          return;
        }
        const teamMembers = result as SimplifiedUser[];
        const filteredNonExisting = teamMembers.filter((s) => !!s?.userId);
        if (!filteredNonExisting.length) {
          this.teamMember.set(NO_VALUE_FOR_TEAM_MEMBER);
          return;
        }
        this.teamMember.set(filteredNonExisting);
      });
  }
}
